import React, { useState } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock, faInfoCircle, faTimes } from '@fortawesome/free-solid-svg-icons';
import { theme } from '../../styles/theme';

const BannerContainer = styled.div`
  position: fixed;
  top: 180px; /* Position below announcement banner (27px) + navbar (120px) + some spacing */
  left: 0;
  right: 0;
  background: ${props => props.theme.colors.offWhite};
  color: ${props => props.theme.colors.primary};
  padding: 1rem 2rem;
  z-index: 100;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  border-bottom: 2px solid ${props => props.theme.colors.accent};
  display: flex;
  align-items: center;
  justify-content: center;
  animation: slideDown 0.5s ease-out forwards;
  
  @keyframes slideDown {
    from {
      transform: translateY(-100%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  @media (max-width: 1024px) {
    top: 160px;
  }

  @media (max-width: 768px) {
    top: 140px;
    padding: 0.8rem 1rem;
    margin-top: 0;
  }
`;

const BannerContent = styled.div`
  max-width: 1200px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    gap: 0.5rem;
  }
`;

const InfoSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const Icon = styled.div`
  font-size: 1.2rem;
  color: ${props => props.theme.colors.accent};
`;

const TextContent = styled.div`
  font-size: 1rem;
  line-height: 1.4;
  
  p {
    margin: 0;
  }
`;

const StatusText = styled.span`
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StatusIndicator = styled.span`
  color: ${props => {
    if (props.$isOpen) return props.theme.colors.success;
    if (props.$openingSoon) return props.theme.colors.warning;
    return props.theme.colors.error;
  }};
  font-weight: 600;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.primary};
  cursor: pointer;
  font-size: 1rem;
  padding: 0.5rem;
  transition: opacity 0.2s ease;
  flex-shrink: 0;
  
  &:hover {
    opacity: 0.8;
  }
`;

const OpeningStatusBanner = ({ castleStatus, gardensStatus, retailStatus }) => {
  const [isVisible, setIsVisible] = useState(true);

  const getStatusText = (status) => {
    if (status.isOpen) return 'Open';
    if (status.openingSoon) return 'Opening Soon';
    return 'Closed';
  };

  // Get current day of week (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
  const currentDay = new Date().getDay();
  const isWeekend = currentDay === 0 || currentDay === 6; // 0 = Sunday, 6 = Saturday
  
  // Check if today is May 5th, 2025
  const today = new Date();
  const isMay5th2025 = today.getFullYear() === 2025 && 
                       today.getMonth() === 4 && // May is month 4 (0-based)
                       today.getDate() === 5;
  
  // Animal Park status - open on weekends and May 5th, 2025
  const animalParkStatus = {
    isOpen: isWeekend || isMay5th2025,
    openingSoon: false
  };

  if (!isVisible) return null;

  return (
    <BannerContainer>
      <BannerContent>
        <InfoSection>
          <Icon>
            <FontAwesomeIcon icon={faClock} />
          </Icon>
          <TextContent>
            <p>
              <StatusText>Today's Opening Status:</StatusText>{' '}
              <StatusIndicator $isOpen={castleStatus.isOpen} $openingSoon={castleStatus.openingSoon}>
                Castle: {getStatusText(castleStatus)}
              </StatusIndicator>{' '}
              •{' '}
              <StatusIndicator $isOpen={gardensStatus.isOpen} $openingSoon={gardensStatus.openingSoon}>
                Gardens: {getStatusText(gardensStatus)}
              </StatusIndicator>{' '}
              •{' '}
              <StatusIndicator $isOpen={retailStatus.isOpen} $openingSoon={retailStatus.openingSoon}>
                Retail: {getStatusText(retailStatus)}
              </StatusIndicator>{' '}
              •{' '}
              <StatusIndicator $isOpen={animalParkStatus.isOpen} $openingSoon={animalParkStatus.openingSoon}>
                Animal Park: {getStatusText(animalParkStatus)}
              </StatusIndicator>
            </p>
          </TextContent>
        </InfoSection>
        <CloseButton onClick={() => setIsVisible(false)}>
          <FontAwesomeIcon icon={faTimes} />
        </CloseButton>
      </BannerContent>
    </BannerContainer>
  );
};

export default OpeningStatusBanner; 